/**
 * Access periods — one row per contiguous period of activity per address.
 *
 * WHY THIS EXISTS. Per-sign-in rows are not a unit of story. On
 * IR-20260818-Controller-001 the 341 sign-ins that survive the filter bar are 341 rows
 * saying the same thing 341 times: a client re-authenticating roughly every 26 minutes.
 * Grouped into contiguous access periods per address they become **6 rows**, and those
 * six narrate the incident — sustained access from one address, three short web logins
 * from the AS136787 addresses, 31 failures as a period closes 41 minutes after the
 * refresh-token revoke, and one lone failed attempt the next day.
 *
 * WHAT WAS TRIED AND REJECTED, so it is not retried:
 *   - per-DAY grouping: 24 rows, but a row can span 24h and the day boundary splits one
 *     continuous burst in two.
 *   - gap-bounded runs WITHOUT a span cap: chaining. Consecutive gaps are p50 26min /
 *     p90 60min, so a 60-minute threshold produced a single **30-hour** row and a
 *     120-minute one produced **51 hours** — worse than per-day, which at least caps at a
 *     day. Any gap-only rule is unsound on this cadence.
 *   - the APP as part of the key: it split the same access period into one row per app
 *     (Microsoft Office / Authentication Broker / OfficeHome). The app is an ATTRIBUTE of
 *     a period, not a reason to start a new one — and keeping it as an attribute still
 *     preserves the real distinction between an interactive web login (OfficeHome, One
 *     Outlook Web) and a token re-auth loop.
 *   - the SESSION as part of the key: sound on the non-interactive feed (40 distinct,
 *     populated) but **empty on 303 of 341 interactive sign-ins (89%)**. Grouping by it
 *     would mostly mean grouping rows whose session is UNKNOWN, and a row must never
 *     imply one session where there is none.
 *
 * An access period is MEANT to span — "continuous access for 51 hours" is the fact being
 * stated. Every constituent event rides on `_periodIds` and `_periodEvents`, and the
 * per-event sub-tabs are untouched, so second-level correlation is always available.
 *
 * The gap threshold is deliberately coarse (default 4h) and the result is insensitive to
 * it: 2h, 4h and 12h all yield exactly 6 periods on the reference case. That is a
 * property of clustered activity, not a tuned parameter.
 */
import { describeTeamsMeeting } from './teamsMeetings.js';

const num = (v) => Number(v) || 0;

/** ms between two event timestamps, or null when either is unparseable. */
function msBetween(a, b) {
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  if (!Number.isFinite(ta) || !Number.isFinite(tb)) return null;
  return tb - ta;
}

export const DEFAULT_ACCESS_GAP_HOURS = 4;

/**
 * A sign-in row that must NEVER be folded into an IP-keyed access period — it is a row
 * that defines a compromise and has to stay visible on its own (mirrors the
 * never-collapse rule for phishing-flagged message-trace rows).
 *
 * Two shapes qualify, from handoff 2026-09-11 (case IR-20260911-clarionhealth-001, the
 * "AiTM Landing Sign-In" HIGH alert):
 *   - `UnattributedRecovery === true` — a raw-UAL relay leg whose UserId was
 *     `Not Available` and was re-attributed by the pipeline. The AiTM landing leg is
 *     exactly this, and it carries the victim's own (proxy-driven) IP, so a plain
 *     IP-keyed rollup buries it inside the victim's normal access period.
 *   - an `ApplicationId` that is a URL rather than a GUID — a phishing-kit landing app
 *     (e.g. `https://portal.microsoftonline.com.orgid.com/`). Caught independently of
 *     the recovery flag so a landing leg that was Graph-enriched still surfaces.
 */
export function isPinnedSignInEvent(e) {
  if (!e || typeof e !== 'object') return false;
  if (e.UnattributedRecovery === true) return true;
  if (/^https?:\/\//i.test(String(e.ApplicationId || ''))) return true;
  return false;
}

/**
 * Group events into access periods.
 *
 * @param events   the event list (already filtered by the caller)
 * @param opts.keyOf        stream identity — defaults to the effective IP
 * @param opts.gapHours     start a new period after this much silence (default 4)
 * @param opts.minSize      periods smaller than this are returned as individual events
 *                          (default 3 — a pair is not worth merging and a merged pair
 *                          can span hours for no reduction)
 * @param opts.countOf      how many underlying authentications one event represents.
 *                          The non-interactive feed arrives pre-aggregated per hour, so
 *                          its rows carry EventCount; interactive sign-ins are 1 each.
 */
export function buildAccessPeriods(events, opts = {}) {
  const list = (Array.isArray(events) ? events : []).filter((e) => e && typeof e === 'object');
  const gapMs = (opts.gapHours ?? DEFAULT_ACCESS_GAP_HOURS) * 3600 * 1000;
  const minSize = opts.minSize ?? 3;
  const keyOf = opts.keyOf || ((e) => e.ResolvedUserIP || e.IPAddress || e.InferredIP || '');
  const countOf = opts.countOf || (() => 1);
  const pinnedOf = opts.pinnedOf || isPinnedSignInEvent;

  const out = [];
  const streams = new Map();
  for (const e of list) {
    // Pinned rows (AiTM landing legs / recovered-attribution rows) are never grouped:
    // emit each as its own row so it cannot be buried inside an IP-keyed period.
    if (pinnedOf(e)) { out.push({ ...e, _period: false, _periodIds: [e.EventId] }); continue; }
    const k = keyOf(e);
    const s = streams.get(k);
    if (s) s.push(e);
    else streams.set(k, [e]);
  }

  for (const [key, members] of streams) {
    const sorted = [...members].sort((a, b) =>
      String(a.EventTimeUTC || '').localeCompare(String(b.EventTimeUTC || '')));
    let run = [sorted[0]];
    const flush = () => {
      if (run.length >= minSize) out.push(makePeriod(key, run, countOf));
      // A run below the threshold is emitted as its own events, untouched, so nothing is
      // reshaped just because it happened to sit near other activity.
      else for (const e of run) out.push({ ...e, _period: false, _periodIds: [e.EventId] });
    };
    for (let i = 1; i < sorted.length; i += 1) {
      const gap = msBetween(sorted[i - 1].EventTimeUTC, sorted[i].EventTimeUTC);
      // An unparseable timestamp must not silently extend a period — break instead.
      if (gap === null || gap > gapMs) { flush(); run = [sorted[i]]; }
      else run.push(sorted[i]);
    }
    flush();
  }
  return out;
}

function makePeriod(key, run, countOf) {
  const first = run[0];
  const last = run[run.length - 1];
  const apps = new Map();
  const sessions = new Set();
  const devices = new Set();
  // 🔴 Error codes must survive into the period. On the reference case the 50173 codes
  // ARE the containment finding -- the attacker's stolen refresh token being rejected the
  // day after the revoke. A period that reported only "N failed" would drop the reason.
  const errorCodes = new Map();
  let auth = 0;
  let ok = 0;
  let bad = 0;
  for (const e of run) {
    const n = countOf(e);
    auth += n;
    // Outcome: prefer explicit counts (non-interactive rows carry them), else the status.
    if (e.SuccessCount !== undefined || e.FailureCount !== undefined) {
      ok += num(e.SuccessCount);
      bad += num(e.FailureCount);
    } else if (String(e.ResultStatus || '').toLowerCase() === 'success') ok += n;
    else if (e.ResultStatus) bad += n;
    const app = e.ApplicationName || '';
    if (app) apps.set(app, (apps.get(app) || 0) + n);
    for (const a of String(e.Apps || '').split(',')) { const t = a.trim(); if (t) apps.set(t, (apps.get(t) || 0) + 1); }
    for (const c of String(e.ErrorCodes || '').split(',')) {
      const t = c.trim();
      // Weight by the row's failure count, falling back to 1 when the row carries a code
      // without a count. Parenthesised deliberately: `a + b || 1` parses as `(a + b) || 1`,
      // which turns a zero-failure row into a count of 1.
      if (t) errorCodes.set(t, (errorCodes.get(t) || 0) + (num(e.FailureCount) || 1));
    }
    const sess = e.SessionId || e.AADSessionId;
    if (sess) sessions.add(sess);
    if (e.DeviceName) devices.add(e.DeviceName);
  }
  const appList = [...apps.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  return {
    ...first,
    LastEventTimeUTC: last.LastEventTimeUTC || last.EventTimeUTC,
    _period: true,
    _periodKey: key,
    _periodIds: run.map((e) => e.EventId),
    _periodEvents: run.map((e) => ({
      eventId: e.EventId,
      startUtc: e.EventTimeUTC,
      endUtc: e.LastEventTimeUTC || e.EventTimeUTC,
      app: e.ApplicationName || e.Apps || '',
      result: e.ResultStatus || e.Outcome || '',
      count: countOf(e),
    })),
    _periodRowCount: run.length,
    _periodAuthCount: auth,
    _periodSuccessCount: ok,
    _periodFailureCount: bad,
    _periodApps: appList.map(([name, n]) => ({ name, count: n })),
    _periodErrorCodes: [...errorCodes.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([code, count]) => ({ code, count })),
    _periodSessions: [...sessions],
    _periodDevices: [...devices].sort(),
    _periodStartUtc: first.EventTimeUTC,
    _periodEndUtc: last.LastEventTimeUTC || last.EventTimeUTC,
  };
}

/** Roll only the matching event type of a mixed stream into access periods. */
export function buildAccessPeriodsInStream(stream, eventType, opts = {}) {
  const list = Array.isArray(stream) ? stream : [];
  const subject = list.filter((e) => e && e._eventType === eventType);
  if (subject.length === 0) return list;
  const periods = buildAccessPeriods(subject, opts);
  const byFirst = new Map();
  const consumed = new Set();
  for (const p of periods) {
    const ids = p._periodIds || [];
    byFirst.set(ids[0], p);
    for (const id of ids.slice(1)) consumed.add(id);
  }
  const out = [];
  for (const e of list) {
    if (!e || e._eventType !== eventType) { out.push(e); continue; }
    if (consumed.has(e.EventId)) continue;
    out.push(byFirst.get(e.EventId) || e);
  }
  return out;
}

/**
 * One-line description of an access period. Says what the period IS — how long access
 * lasted, how much of it succeeded, and through which applications — and never who was
 * responsible: attribution comes from the analyst's IP classification, which colours the
 * row through the normal mechanism.
 */
export function describeAccessPeriod(period, nounSingular = 'sign-in') {
  const p = period || {};
  // 🔴 A row that is NOT a period has no period aggregates, so computing from them
  // yields "0 sign-ins" -- a plausible-looking wrong number on a row that represents a
  // real event. Return null so the caller must fall back to the normal per-event
  // description rather than printing a zero. (Caught on real data: the single failed
  // sign-in of 2026-08-04 rendered as "0 sign-ins".)
  if (!p._period) return null;
  const n = num(p._periodAuthCount);
  const ok = num(p._periodSuccessCount);
  const bad = num(p._periodFailureCount);
  const noun = n === 1 ? nounSingular : `${nounSingular}s`;
  let outcome;
  if (ok && bad) outcome = ` — ${ok} succeeded, ${bad} failed`;
  else if (bad && !ok) outcome = bad === n ? ' — all failed' : ` — ${bad} failed`;
  else if (ok) outcome = ' — all succeeded';
  else outcome = '';
  const apps = (p._periodApps || []).slice(0, 3)
    .map((a) => (a.count > 1 ? `${a.name} ×${a.count}` : a.name)).join(', ');
  const more = (p._periodApps || []).length > 3 ? ` +${p._periodApps.length - 3} more` : '';
  const via = apps ? ` via ${apps}${more}` : '';
  const span = spanLabel(p._periodStartUtc, p._periodEndUtc);
  const codes = (p._periodErrorCodes || []).length
    ? ` [${p._periodErrorCodes.slice(0, 3).map((c) => c.code).join(', ')}]`
    : '';
  return `${n} ${noun}${span}${outcome}${codes}${via}`;
}

/**
 * A span as a DURATION, never as clock times.
 *
 * Exported because describeNonInteractive needs the same phrasing: it used to print the
 * raw UTC clock ("between 19:20 and 19:50 UTC") while the row's own timestamp column is
 * rendered in the analyst's chosen zone, so one report row showed both "3:20 PM" and
 * "19:20" for the same moment. A duration cannot disagree with a timezone.
 */
export function spanLabel(startUtc, endUtc) {
  const ms = msBetween(startUtc, endUtc);
  if (ms === null || ms <= 0) return '';
  const h = ms / 3600000;
  if (h < 1) return ` over ${Math.max(1, Math.round(ms / 60000))} min`;
  if (h < 24) return ` over ${h.toFixed(1)} h`;
  return ` over ${(h / 24).toFixed(1)} days`;
}

/**
 * The description for any row that a story roll-up produced, or null when the row is an
 * ordinary event and the caller should use its normal description.
 *
 * ONE function called by BOTH copies of generateDescription (eventUtils.js and
 * TimelineView.jsx). Those copies have diverged before, and a rolled row must not
 * describe itself one way on screen and another in a report.
 */
export function describeRolledRow(event) {
  const e = event || {};
  if (e._period) {
    return describeAccessPeriod(e, e._eventType === 'NonInteractiveSignIn' ? 'authentication' : 'sign-in');
  }
  if (e._meeting) return describeTeamsMeeting(e);
  return null;
}
